/* eslint no-shadow: ["error", { "allow": ["state", "getters"] }] */
import { groupBy } from 'lodash';
import TanksService from '../../services/TanksService';
import Idb from '../../services/IndexedDbService';
import ErrorHandlerHelpers from '../../mixins/ErrorHandlerHelpers';

const SET_TANKS_WITH_CURRENT_STATUS = 'SET_TANKS_WITH_CURRENT_STATUS';
const CLEAR_TANKS_WITH_CURRENT_STATUS = 'CLEAR_TANKS_WITH_CURRENT_STATUS';

const SET_VESSEL_TANKS = 'SET_VESSEL_TANKS';
const CLEAR_VESSEL_TANKS = 'CLEAR_VESSEL_TANKS';

const SET_TANK_DETAILS = 'SET_TANK_DETAILS';
const CLEAR_TANK_DETAILS = 'CLEAR_TANK_DETAILS';

const state = {
  tanksWithCurrentStatus: [],
  vesselTanks: [],
  tankDetails: null,
  tankUnitOfMeasurements: ['m3', 'Tonnes', 'bbl'],
};

const getters = {
  getSyncedAndUnsyncedTankStatuses: (state, getters, rootState, rootGetters) => {
    const syncedStatuses = state.tanksWithCurrentStatus;
    const unsyncedStatuses = rootGetters['dataSyncStore/getLatestUnsyncedTankStatuses'];

    // Filter out duplicate isLatest synced/unsynced statuses
    const unsyncedLatestStatusTankIds = unsyncedStatuses
      .filter((x) => x.currentTankStatus.isLatest).map((x) => x.id);
    const filteredSyncedStatuses = syncedStatuses
      .filter((x) => !unsyncedLatestStatusTankIds.includes(x.id));

    // re-sort and return
    const result = [].concat(unsyncedStatuses).concat(filteredSyncedStatuses);
    const sortedResult = result.sort((a, b) => {
      if (a.tankName > b.tankName) return 1;
      if (a.tankName < b.tankName) return -1;
      return 0;
    });

    return sortedResult;
  },
  getTankStatusesByGroup: (state) => (groupString) => groupBy(state.tankStatuses,
    (x) => x[groupString]),
  getSyncedAndUnsyncedTankStatusesByGroup: (state, getters) => (groupString) => groupBy(
    getters.getSyncedAndUnsyncedTankStatuses, (x) => x[groupString],
  ),
  getVesselTanksWithCurrentStatus: (state, getters) => (vesselId) => getters
    .getSyncedAndUnsyncedTankStatuses.filter((x) => x.vessel.id === vesselId),
  getVesselTanksWithStatusForVesselConsumption: (state, getters) => (vesselId) => getters
    .getSyncedAndUnsyncedTankStatuses.filter((x) => (x.vessel.id === vesselId)
      && (x.tankBulkType.toLowerCase() === 'water' || x.tankBulkType.toLowerCase() === 'fuel')),
  getTankUnitOfMeasurements: (state) => state.tankUnitOfMeasurements,
};

const mutations = {
  [SET_TANKS_WITH_CURRENT_STATUS](state, tanksWithCurrentStatus) {
    state.tanksWithCurrentStatus = tanksWithCurrentStatus;
  },
  [CLEAR_TANKS_WITH_CURRENT_STATUS](state) {
    state.tanksWithCurrentStatus = null;
  },
  [SET_VESSEL_TANKS](state, vesselTanks) {
    state.vesselTanks = vesselTanks;
  },
  [CLEAR_VESSEL_TANKS](state) {
    state.vesselTanks = null;
  },
  [SET_TANK_DETAILS](state, tankDetails) {
    state.tankDetails = tankDetails;
  },
  [CLEAR_TANK_DETAILS](state) {
    state.tankDetails = null;
  },
};

const actions = ({
  loadTankStatuses({ commit }, activeSiteId) {
    return TanksService.getTanksWithCurrentStatus(activeSiteId)
      .then((x) => x.data)
      .then((tankStatesResult) => {
        commit(SET_TANKS_WITH_CURRENT_STATUS, tankStatesResult.tanksWithStatus);
      }).catch((err) => ErrorHandlerHelpers.handleServiceError(err));
  },
  loadVesselTanks({ commit }, activeSiteId) {
    return TanksService.getVesselTanks(activeSiteId)
      .then((x) => x.data)
      .then((tankResult) => {
        commit(SET_VESSEL_TANKS, tankResult.vesselTanks);
      }).catch((err) => ErrorHandlerHelpers.handleServiceError(err));
  },
  loadTankDetails({
    commit, state, getters, rootState, rootGetters,
  }, tankId) {
    const online = rootState.userStore.isClientConnectedToServer;
    if (online) {
      return TanksService.getTankDetails(tankId)
        .then((x) => x.data)
        .then((tankResult) => {
          commit(SET_TANK_DETAILS, tankResult);
        }).catch((err) => ErrorHandlerHelpers.handleServiceError(err));
    }

    // Offline
    // Get synced and unsynced statuses for tank history
    const unsyncedTankStatuses = rootGetters['dataSyncStore/getUnsyncedTankStatuses'];
    const syncedTankStatuses = state.tanksWithCurrentStatus.filter((x) => x.id === tankId);
    const syncedAndUnsyncedStatuses = unsyncedTankStatuses.concat(syncedTankStatuses);

    // Get current tank status
    const tankAndCurrentStatus = getters.getSyncedAndUnsyncedTankStatuses
      .find((x) => x.id === tankId && x.currentTankStatus.isLatest);

    // Tank history
    const tankStatusHistory = syncedAndUnsyncedStatuses
      .filter((x) => x.id === tankId)
      .map((x) => (
        { ...x.currentTankStatus, id: x.id, idc: x.idc }
      ));

    // Tank Details (Tank and all past tank history from IndexedDb)
    const details = {
      tank: { ...tankAndCurrentStatus },
      tankStatusHistory,
    };

    commit(SET_TANK_DETAILS, details);
    return Promise.resolve();
  },
  createTank({ dispatch }, tank) {
    return TanksService.createTank(tank)
      .then((x) => x.data)
      .then((tankResults) => {
        dispatch('loadVesselTanks', tankResults.tank.siteId);
      }).catch((err) => ErrorHandlerHelpers.handleServiceError(err));
  },
  updateTank({ dispatch }, tankUpdate) {
    return TanksService.updateTank(tankUpdate)
      .then((x) => x.data)
      .then((updateResult) => {
        dispatch('loadVesselTanks', updateResult.siteId);
      }).catch((err) => ErrorHandlerHelpers.handleServiceError(err));
  },
  deleteTank({ dispatch, rootState }, id) {
    const activeSiteId = rootState.userStore.activeSite
      ? rootState.userStore.activeSite.id
      : rootState.userStore.user.activeSite.id;

    return TanksService.deleteTank(id)
      .then((x) => dispatch('loadVesselTanks', activeSiteId))
      .catch((err) => ErrorHandlerHelpers.handleServiceError(err));
  },

  // These need to changed to "[create|delete]ManualTanksStatus[Offline]"
  createTankStatus({ dispatch, rootState }, payload) {
    const { tankStatus, manualRecording } = payload;
    const online = rootState.userStore.isClientConnectedToServer;
    const activeSiteId = rootState.userStore.activeSite
      ? rootState.userStore.activeSite.id
      : rootState.userStore.user.activeSite.id;

    if (online) {
      if (manualRecording) {
        return TanksService.createTankStatus(tankStatus)
          .then(() => dispatch('loadTankStatuses', activeSiteId))
          .catch((err) => ErrorHandlerHelpers.handleServiceError(err));
      }

      return TanksService.createTankStatusFromTransaction(tankStatus)
        .then(() => dispatch('loadTankStatuses', activeSiteId))
        .catch((err) => ErrorHandlerHelpers.handleServiceError(err));
    }

    // Offline Storage for syncing later
    return dispatch('createTankStatusOffline', tankStatus);
  },
  createTankStatusOffline({ dispatch, getters }, tankStatus) {
    // calculate fill percentage
    const quantityInt = parseInt(tankStatus.quantityOnBoard, 10);
    const fillPercent = ((quantityInt / (tankStatus.tank.maxCapacity)) * 100).toFixed(0);

    // map to TankWithStatus structure
    const tankWithCurrentStatus = {
      ...tankStatus.tank,
      currentTankStatus: {
        ...tankStatus,
        id: null,
        fillPercent,
        isLatest: false,
      },
    };

    // Check is new status isLatest = true
    const latestTankStatus = getters.getSyncedAndUnsyncedTankStatuses
      .find((x) => x.id === tankWithCurrentStatus.id);

    if (latestTankStatus) {
      const previousStatusDateTimeLt = latestTankStatus.currentTankStatus.statusDateTimeLt;
      const newStatusDateTimeLt = tankWithCurrentStatus.currentTankStatus.statusDateTimeLt;

      // Check if new status is most recent (set isLatest=true if so)
      if (newStatusDateTimeLt >= previousStatusDateTimeLt) {
        tankWithCurrentStatus.currentTankStatus.isLatest = true;

        // Update previous status if it is unsynced
        if (latestTankStatus.idc) {
          latestTankStatus.currentTankStatus.isLatest = false;
          Idb.updateTankStatus(latestTankStatus);
        }
      }
    } else tankWithCurrentStatus.currentTankStatus.isLatest = true;

    // Save Tank Status
    return Idb.saveTankStatus(tankWithCurrentStatus).then(() => dispatch('dataSyncStore/loadTankStatusesToBeSynced', null, { root: true }));
  },
  deleteTankStatus({ dispatch, rootState }, params) {

    // TODO: Delete Linked Bulk Transaction as well
    // 

    const { id, idc } = params;
    const online = rootState.userStore.isClientConnectedToServer;
    if (online && id && !idc) {
      const activeSiteId = rootState.userStore.activeSite
        ? rootState.userStore.activeSite.id
        : rootState.userStore.user.activeSite.id;
      return TanksService.deleteTankStatus(id)
        .then((x) => dispatch('loadVesselTanks', activeSiteId))
        .catch((err) => ErrorHandlerHelpers.handleServiceError(err));
    }

    const offlineStatus = rootState.dataSyncStore.tanksWithCurrentStatusToBeSynced
      .find((x) => x.idc === id || x.id === id);

    return Idb.deleteTankStatus(offlineStatus).then(() => dispatch('dataSyncStore/loadTankStatusesToBeSynced', null, { root: true }));
  },

  clearTankDetails({ commit }) {
    commit(CLEAR_TANK_DETAILS);
  },
  cascadeTankStatus() {

  },
  correctIsLatest() {

  },
});

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
